import { IBackgroundJob, IBackgroundJobProgress } from '@models/backgroundJobs/IBackgroundJob';
import { plural } from '@srm/ui/lib/pluralize';
import { backgroundJobKind, COMMON_PROGRESS_UNITS, COMMON_STAGE_LABELS } from './backgroundJobKinds';

/** Подпись этапа: своя у вида, затем общая; неизвестный этап — `null`. */
export const stageLabel = (job: Pick<IBackgroundJob, 'kind' | 'stage'>): string | null => {
    if (!job.stage) return null;
    return backgroundJobKind(job.kind).stageLabels[job.stage] ?? COMMON_STAGE_LABELS[job.stage] ?? null;
};

/** Состояние задачи словами: этап идущей, итог закончившейся. */
export const jobStateLabel = (job: IBackgroundJob): string => {
    switch (job.status) {
        case 'queued':
            return COMMON_STAGE_LABELS.queue;
        case 'committing':
            return COMMON_STAGE_LABELS.commit;
        case 'running':
            return stageLabel(job) ?? 'Выполняется';
        case 'succeeded':
            return 'Готово';
        case 'failed':
            return backgroundJobKind(job.kind).failedLabel;
        case 'cancelled':
            return 'Отменено';
    }
};

/** Место в очереди: «перед вами 2 задачи» / «следующая»; без сведений — пусто. */
export const queuePositionLabel = (position: number | null | undefined): string | null => {
    if (position == null) return null;
    if (position <= 0) return 'следующая';
    return `перед вами ${position} ${plural(position, ['задача', 'задачи', 'задач'])}`;
};

/** Сколько такая задача обычно идёт: «обычно ~3 мин» неразрывно; без сведений — пусто. */
export const expectedDurationLabel = (expectedMs: number | null | undefined): string | null => {
    if (expectedMs == null || !Number.isFinite(expectedMs) || expectedMs <= 0) return null;
    const minutes = Math.round(expectedMs / 60_000);
    return minutes < 1 ? 'обычно\u00a0меньше\u00a0минуты' : `обычно\u00a0~${minutes}\u00a0мин`;
};

const formatCount = (value: number) => Math.max(0, Math.round(value)).toLocaleString('ru-RU');

/** Ход в цифрах: «1 240 из 8 000 строк», без общего числа — «1 240 строк»; без сведений — пусто. */
export const progressLabel = (
    kind: IBackgroundJob['kind'],
    progress: IBackgroundJobProgress | null | undefined,
): string | null => {
    if (!progress || !Number.isFinite(progress.done)) return null;
    const unit = progress.unit
        ? (backgroundJobKind(kind).progressUnits[progress.unit] ?? COMMON_PROGRESS_UNITS[progress.unit])
        : undefined;
    const { done, total } = progress;
    if (total != null && Number.isFinite(total)) {
        const tail = total % 10 === 1 && total % 100 !== 11 ? unit?.of[0] : unit?.of[1];
        return `${formatCount(done)} из ${formatCount(total)}${tail ? ` ${tail}` : ''}`;
    }
    return `${formatCount(done)}${unit ? ` ${plural(done, unit.count)}` : ''}`;
};

/** Доля сделанного 0…1 для определённой дуги; без общего числа — `null`. */
export const progressFraction = (progress: IBackgroundJobProgress | null | undefined): number | null => {
    if (!progress?.total || progress.total <= 0 || !Number.isFinite(progress.done)) return null;
    return Math.min(1, Math.max(0, progress.done / progress.total));
};

/** Ход идущей задачи одной строкой: этап, место в очереди или прогресс в цифрах. */
export const jobProgressLabel = (job: IBackgroundJob): string => {
    const label = jobStateLabel(job);
    const detail =
        job.status === 'queued'
            ? queuePositionLabel(job.queuePosition)
            : job.status === 'running'
              ? progressLabel(job.kind, job.progress)
              : null;
    return detail ? `${label} · ${detail}` : label;
};

/** Секундомер: 0:07, 1:12, 1:02:05. */
export const formatElapsed = (ms: number): string => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = String(total % 60).padStart(2, '0');
    return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
};
